import { useEffect, useState } from 'react';
import { forwardRef, Box, Icon, Text, Tooltip, useResponsive } from '@bitrise/bitkit';
import { useInterval } from 'usehooks-ts';
import { TypeIconName } from '@bitrise/bitkit/src/Components/Icon/Icon';
import {
  Build,
  calculateDuration,
  getBuildStatusColors,
  isBuildRunning,
  isPullRequest,
  sourceBranch,
  targetBranch,
} from '@/models/Build';
import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import { formatDateTime, formatDuration } from '@/utils/commonFormats';
import { secondsToDurationString } from '@/utils/secondsToDurationString';

type LowerCardContentDetailsProps = { build: Build };
const BuildTagOrBranches = ({ build }: LowerCardContentDetailsProps): JSX.Element | null => {
  if (!build.tag && !build.branch && !build.pullRequestTargetBranch) {
    return null;
  }

  const statusColors = getBuildStatusColors(build);
  if (build.tag) {
    return (
      <BuildMetaData
        display="flex"
        alignItems="center"
        paddingX="12"
        paddingTop="8"
        paddingBottom="10"
        gap="4"
        overflow="hidden"
        textColor={statusColors.textColor}
        backgroundColor={statusColors.backgroundColor}
        _groupHover={{ backgroundColor: statusColors.hoverBackgroundColor }}
      >
        <Icon name="Tag" color={statusColors.textColor} flexShrink={0} width="16px" height="16px" />
        <Text size="2" hasEllipsis>
          {build.tag}
        </Text>
      </BuildMetaData>
    );
  }
  return (
    <BuildMetaData
      display="flex"
      alignItems="center"
      paddingX="12"
      paddingTop="8"
      paddingBottom="10"
      gap="4"
      overflow="hidden"
      textColor={statusColors.textColor}
      backgroundColor={statusColors.backgroundColor}
      _groupHover={{ backgroundColor: statusColors.hoverBackgroundColor }}
    >
      <Icon
        name={isPullRequest(build) ? 'Pull' : 'Push'}
        color={statusColors.textColor}
        flexShrink={0}
        width="16px"
        height="16px"
      />
      <>
        {isPullRequest(build) && (
          <Text flexShrink={1} size="2" hasEllipsis>
            {sourceBranch(build)}
          </Text>
        )}
        {isPullRequest(build) && <Icon name="ArrowMoveRight" flexShrink={0} width="16px" height="16px" />}
        <Text flexShrink={1} size="2" hasEllipsis>
          {targetBranch(build)}
        </Text>
      </>
    </BuildMetaData>
  );
};

const BuildWorkflowName = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  if (!build.triggeredWorkflow && !build.isPipeline) {
    return null;
  }

  const iconName: TypeIconName = build?.isPipeline ? 'WorkflowFlow' : 'Workflow';
  const tooltipLabel: string = build?.isPipeline ? 'Pipeline' : 'Workflow';

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      paddingX="12"
      paddingTop="8"
      paddingBottom="10"
      paddingRight="12"
    >
      <Tooltip label={tooltipLabel}>
        <BuildMetaData
          icon={iconName}
          justifyContent="flex-start"
          minWidth={['100%', '120px']}
          alignItems="center"
          gap="4"
          color="neutral.40"
          textColor="neutral.40"
          ref={ref}
          zIndex={1}
        >
          <Text size="2" hasEllipsis>
            {build?.triggeredWorkflow}
          </Text>
        </BuildMetaData>
      </Tooltip>
    </Box>
  );
});

const BuildStartedAt = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  if (!build?.triggeredAt) {
    return null;
  }

  return (
    <Tooltip label="Build started at">
      <BuildMetaData icon="Clock" color="neutral.40" textColor="neutral.40" ref={ref} zIndex={1}>
        <Text size="2" lineHeight="16px">
          <time dateTime={build.triggeredAt} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(build.triggeredAt, { hideYear: true, hideSeconds: true })}
          </time>
        </Text>
      </BuildMetaData>
    </Tooltip>
  );
});

const BuildDuration = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  const [duration, setDuration] = useState(() => (build && calculateDuration(build)) || 0);
  const isUpdating = build && isBuildRunning(build);

  useEffect(() => {
    if (build) {
      setDuration(calculateDuration(build));
    }
  }, [build, isUpdating]);

  useInterval(
    () => {
      if (build) {
        setDuration(calculateDuration(build));
      }
    },
    isUpdating ? 1000 : null,
  );

  if (duration === 0) {
    return null;
  }

  return (
    <Tooltip label="Duration">
      <BuildMetaData icon="Stopwatch" color="neutral.40" textColor="neutral.40" minWidth="32" ref={ref} zIndex={1}>
        <Text size="2" lineHeight="16px">
          <time dateTime={secondsToDurationString(duration)} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDuration(duration)}
          </time>
        </Text>
      </BuildMetaData>
    </Tooltip>
  );
});

const BuildCredits = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  if (!build.creditCost) {
    return null;
  }

  return (
    <Tooltip label="Credit cost">
      <BuildMetaData icon="Credits" color="neutral.40" textColor="neutral.40" ref={ref} zIndex={1}>
        {build.creditCost}
      </BuildMetaData>
    </Tooltip>
  );
});

const BuildNumber = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  if (!build.buildNumber) {
    return null;
  }

  return (
    <Tooltip label="Build number">
      <BuildMetaData icon="Numero" color="neutral.40" textColor="neutral.40" gap="0" minWidth="0" ref={ref} zIndex={1}>
        {build.buildNumber}
      </BuildMetaData>
    </Tooltip>
  );
});

const BuildPriority = forwardRef(({ build }: LowerCardContentDetailsProps, ref): JSX.Element | null => {
  if (build.triggerParams?.priority === undefined || build.triggerParams.priority === 0) return null;

  return (
    <Tooltip label={`Priority: ${build.triggerParams.priority}`}>
      <BuildMetaData icon="Flag" gap="0" minWidth="0" ref={ref} zIndex={1} color="neutral.40" textColor="neutral.40">
        <Text size="2" paddingStart={4}>
          {build.triggerParams.priority > 0 ? 'High' : 'Low'}
        </Text>
      </BuildMetaData>
    </Tooltip>
  );
});

type BuildListItemLowerSectionProps = {
  build: Build;
  showBuildNumberOnMobile: boolean;
};
const BuildItemLowerSection = ({ build, showBuildNumberOnMobile }: BuildListItemLowerSectionProps): JSX.Element => {
  const { isMobile } = useResponsive();
  const hasCaching = build.buildToolInvocations && build.buildToolInvocations[0];

  return (
    <Box display="flex" flexDirection={['column', 'row']}>
      <BuildTagOrBranches build={build} />
      <BuildWorkflowName build={build} />
      <Box
        display="flex"
        flexDirection="row"
        flexShrink={0}
        flexWrap={['wrap', 'nowrap']}
        alignItems={['flex-start', 'center']}
        justifyContent={['flex-start', 'flex-end']}
        gap="12"
        paddingTop="8"
        paddingRight="24"
        paddingBottom="10"
        paddingLeft="12"
        flexGrow={1}
        borderTop={['1px solid', 'none']}
        borderTopColor="neutral.93"
      >
        {hasCaching && (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent={['flex-start', 'flex-end']}
            gap="12"
            flexGrow={1}
          >
            {build.buildToolInvocations && build.buildToolInvocations[0] && (
              <Tooltip label="Cache connected">
                <BuildMetaData icon="BuildCache" gap={0} color="neutral.40" textColor="neutral.40" zIndex={1} />
              </Tooltip>
            )}
          </Box>
        )}
        <BuildPriority build={build} />
        <BuildStartedAt build={build} />
        <BuildDuration build={build} />
        <BuildCredits build={build} />
        {(!isMobile || showBuildNumberOnMobile) && <BuildNumber build={build} />}
      </Box>
    </Box>
  );
};

export default BuildItemLowerSection;
